import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { Button } from '@speeki/global-ui-components';
import { TextField } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';
import { ColumnsLayout } from '@components/templates/ColumnsLayout/ColumnsLayout';

import { useSetupMfa } from '@services/AuthService/useSetupMfa';
import { getPath } from '@services/RouteService/paths';

import { CardStep } from '@domains/auth/atoms/CardStep/CardStep';
import { MfaCard, MfaCardStepWrapper } from '@domains/auth/ions/cards';
import { yupResolver } from '@hookform/resolvers/yup';
import { emailValidation } from '@shared/validations';

export const SetupMfaEmailPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { handleSetup, setupError, setupMfaProcessing } = useSetupMfa();

    const {
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
        register,
        setError,
    } = useForm<MfaEmailFormValues>({
        mode: 'onChange',
        resolver: yupResolver(MfaEmailFormSchema),
    });

    const onSubmit = handleSubmit(async ({ email }) => {
        await handleSetup(
            {
                email,
            },
            {
                onSuccess: () =>
                    navigate(getPath({ path: '/auth/successful-mfa-setup' })),
            },
        );
    });

    useEffect(() => {
        setupError?.graphQLErrors[0].extensions?.code === 'BAD_USER_INPUT' &&
            setError('email', {
                message: 'MFA_INVALID_SECONDARY_EMAIL',
                type: 'invalid-email',
            });
    }, [setupError]);

    return (
        <ColumnsLayout>
            <MfaCard testId="QE6rRSg">
                <MfaCard.TitleSecondary>
                    {t('SETUP_MFA_HEADER_FIRST_LINE')}
                    <br /> {t('SETUP_MFA_HEADER_SECOND_LINE')}
                </MfaCard.TitleSecondary>
                <MfaCardStepWrapper>
                    <CardStep
                        text={t('SETUP_MFA_FOURTH_STEP_TEXT')}
                        title={t('SETUP_MFA_FOURTH_STEP_TITLE')}
                    />
                </MfaCardStepWrapper>
                <StandardForm noValidate>
                    <TextField
                        {...register('email')}
                        className="htmlToImageIgnore"
                        error={!!errors.email}
                        label={t('SECONDARY_EMAIL_ADDRESS')}
                        message={t(errors.email?.message || '')}
                        placeholder={t('ENTER_SECONDARY_EMAIL')}
                        testId="hq18Gaz"
                        type="email"
                    />

                    <TextField
                        {...register('confirmEmail')}
                        className="htmlToImageIgnore"
                        error={!!errors.confirmEmail}
                        label={t('CONFIRM_SECONDARY_EMAIL')}
                        message={t(errors.confirmEmail?.message || '')}
                        placeholder={t('RE_ENTER_SECONDARY_EMAIL')}
                        testId="zvkmqjG"
                        type="email"
                    />
                    <StandardForm.Actions>
                        <Button
                            disabled={isSubmitting || (!isValid && isSubmitted)}
                            fullWidth
                            isLoading={setupMfaProcessing}
                            onClick={onSubmit}
                            testId="button_confirm"
                            variant="primary"
                        >
                            {t('CONFIRM')}
                        </Button>
                    </StandardForm.Actions>
                </StandardForm>
            </MfaCard>
        </ColumnsLayout>
    );
};

const MfaEmailFormSchema = yup.object({
    confirmEmail: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .oneOf([yup.ref('email')], 'EMAIL_NOT_MATCH'),
    email: emailValidation,
});

type MfaEmailFormValues = yup.InferType<typeof MfaEmailFormSchema>;
